import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { waitlistAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { 
  Clock, 
  Users, 
  Phone, 
  Mail,
  CheckCircle,
  Loader2,
  MapPin,
  QrCode,
  RefreshCw
} from 'lucide-react';

export default function WaitlistRegister() {
  const { restaurantId } = useParams();
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    party_size: 2,
    special_requests: '',
  });

  useEffect(() => {
    fetchRestaurantInfo();
  }, [restaurantId]);

  const fetchRestaurantInfo = async () => {
    try {
      setLoading(true);
      const response = await waitlistAPI.getQRInfo(restaurantId);
      setRestaurantInfo(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('No se pudo cargar la información del restaurante');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.guest_name || !formData.guest_phone) {
      toast.error('Por favor completa nombre y teléfono');
      return;
    }
    
    try {
      setSubmitting(true);
      // Don't send empty email (backend expects null, not empty string for EmailStr)
      const dataToSend = {
        ...formData,
        guest_email: formData.guest_email?.trim() || null,
      };
      const response = await waitlistAPI.publicRegister(restaurantId, dataToSend);
      setRegistrationResult(response.data);
      setSubmitted(true);
      toast.success('¡Registrado exitosamente!');
    } catch (error) {
      // Handle Pydantic validation errors (array of error objects)
      const detail = error.response?.data?.detail;
      let errorMessage = 'Error al registrarse';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Extract message from first validation error
        errorMessage = detail[0]?.msg || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const checkStatus = async () => {
    if (!registrationResult?.qr_code) return;
    
    try {
      setCheckingStatus(true);
      const response = await waitlistAPI.checkStatus(registrationResult.qr_code);
      setRegistrationResult(prev => ({
        ...prev,
        ...response.data
      }));
      
      if (response.data.notified) {
        toast.success('¡Tu mesa está lista! Por favor acércate a la recepción');
      }
    } catch (error) {
      toast.error('Error al verificar estado');
    } finally {
      setCheckingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!restaurantInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive">Restaurante no encontrado</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!restaurantInfo.is_accepting_waitlist) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Lista de espera cerrada</h2>
            <p className="text-muted-foreground">
              {restaurantInfo.restaurant_name} no está aceptando lista de espera en este momento.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show status after registration
  if (submitted && registrationResult) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className={cn(
              "w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4",
              registrationResult.notified 
                ? "bg-green-500 text-white" 
                : "bg-primary/10 text-primary"
            )}>
              {registrationResult.notified ? (
                <CheckCircle className="h-10 w-10" />
              ) : (
                <span className="text-3xl font-bold">{registrationResult.position}</span>
              )}
            </div>
            <CardTitle className="text-2xl">
              {registrationResult.notified ? '¡Tu mesa está lista!' : '¡Registrado!'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {registrationResult.notified ? (
              <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-xl">
                <p className="text-green-700 dark:text-green-300 font-medium">
                  Por favor acércate a la recepción
                </p>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <p className="text-muted-foreground">
                    Hola <strong>{registrationResult.guest_name}</strong>, estás en la lista de espera de
                  </p>
                  <p className="text-xl font-semibold text-primary mt-1">
                    {restaurantInfo.restaurant_name}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-muted text-center">
                    <p className="text-3xl font-bold text-primary">{registrationResult.position}</p>
                    <p className="text-sm text-muted-foreground">Tu posición</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted text-center">
                    <p className="text-3xl font-bold">~{registrationResult.estimated_wait_minutes}</p>
                    <p className="text-sm text-muted-foreground">Minutos aprox.</p>
                  </div>
                </div>

                <div className="p-4 bg-primary/5 rounded-xl">
                  <p className="text-sm text-center text-muted-foreground">
                    Te enviaremos un <strong>SMS</strong> cuando tu mesa esté lista.
                    <br />
                    No te alejes demasiado del restaurante.
                  </p>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full rounded-xl"
                  onClick={checkStatus}
                  disabled={checkingStatus}
                >
                  {checkingStatus ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Actualizar estado
                </Button>
              </>
            )}

            <p className="text-xs text-center text-muted-foreground">
              Código: {registrationResult.qr_code}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Registration form
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-xl bg-primary/10 mx-auto flex items-center justify-center mb-4">
            <QrCode className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">{restaurantInfo.restaurant_name}</CardTitle>
          {restaurantInfo.restaurant_address && (
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-1 mt-1">
              <MapPin className="h-3 w-3" />
              {restaurantInfo.restaurant_address}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {/* Current wait info */}
          <div className="mb-6 p-4 rounded-xl bg-muted">
            <div className="flex items-center justify-between">
              <div className="text-center">
                <p className="text-2xl font-bold">{restaurantInfo.current_queue_size}</p>
                <p className="text-xs text-muted-foreground">En espera</p>
              </div>
              <div className="h-10 w-px bg-border" />
              <div className="text-center">
                <p className="text-2xl font-bold">~{restaurantInfo.estimated_wait_minutes}</p>
                <p className="text-xs text-muted-foreground">Min. aprox.</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Tu nombre *</Label>
              <Input
                value={formData.guest_name}
                onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                placeholder="Nombre completo"
                className="rounded-xl"
                required
              />
            </div>

            <div>
              <Label>Teléfono celular *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={formData.guest_phone}
                  onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  className="rounded-xl pl-10"
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Te enviaremos un SMS cuando tu mesa esté lista
              </p>
            </div>

            <div>
              <Label>Número de personas *</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={formData.party_size}
                  onChange={(e) => setFormData({ ...formData, party_size: parseInt(e.target.value) || 1 })}
                  className="rounded-xl pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Email (opcional)</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={formData.guest_email}
                  onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  className="rounded-xl pl-10"
                />
              </div>
            </div>

            <div>
              <Label>Peticiones especiales (opcional)</Label>
              <Textarea
                value={formData.special_requests}
                onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                placeholder="Silla alta, cumpleaños, preferencia de zona..."
                rows={2}
                className="rounded-xl"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full rounded-xl h-12 text-lg"
              disabled={submitting || !formData.guest_name || !formData.guest_phone}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Registrando...
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 mr-2" />
                  Unirme a la lista de espera
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground mt-4">
            Al registrarte, aceptas recibir notificaciones SMS sobre tu lugar en la lista.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
